/**
 * The world, assembled: the town (a grid of district plots, see town/) plus
 * every interior (see interiors/). The server reads this to validate
 * movement and seats; the client reads it to draw. Server and every client
 * build the identical map, deterministically.
 *
 * Interiors draw their back (y = 0) and left (x = 0) walls.
 */
import { GROUNDS, KINDS, WATER, footprint, type Ground, type Npc, type Portal, type RoomDef, type RoomId } from './defs.ts'
import { INTERIOR_NAMES, INTERIORS } from './interiors/index.ts'
import { compose, type World } from './town/plot.ts'
import { PLOTS, SPAWN_PLACE } from './town/index.ts'

export * from './defs.ts'

const town: World = compose(PLOTS, INTERIOR_NAMES)
const spawn = town.places.find((p) => p.id === SPAWN_PLACE) ?? town.places[0]!

const townRoom: RoomDef & { groundMap: Uint8Array } = {
  id: 'town', name: 'Blobland', blurb: 'The whole town, out in the open air.',
  w: town.w, d: town.d, outdoor: true, ground: 'grass', accent: '#6cc47f',
  zones: [], groundMap: town.ground,
  spawn: [spawn.x, spawn.y], props: town.props, walls: [], portals: town.portals,
  critters: town.critters, places: town.places, spots: town.spots, ambience: town.ambience,
  cap: 100, softCap: 80,
}

export const ROOMS: Record<RoomId, RoomDef> = {
  town: townRoom,
  ...(Object.fromEntries(INTERIORS.map((i) => [i.room.id, i.room])) as Record<Exclude<RoomId, 'town'>, RoomDef>),
}
export const ROOM_IDS = Object.keys(ROOMS) as RoomId[]
export const DEFAULT_ROOM: RoomId = 'town'

/** Every NPC, by room, with town NPCs already in world coordinates. */
export const ROOM_NPCS: Record<RoomId, Npc[]> = {
  town: town.npcs,
  ...(Object.fromEntries(INTERIORS.map((i) => [i.room.id, i.npcs])) as Record<Exclude<RoomId, 'town'>, Npc[]>),
}

/** The districts of the town, in world tiles, for the minimap. */
export const DISTRICTS = town.plots

export function isRoomId(v: unknown): v is RoomId {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(ROOMS, v)
}

// ─── Compiled grid ─────────────────────────────────────────────────────────

/** Tile flags. */
export const BLOCKED = 0
export const OPEN = 1
/** Walkable only as the end of a path: you sit on it, you don't walk through it. */
export const SEAT = 2

export interface Grid {
  w: number
  d: number
  cells: Uint8Array
  /** Ground per tile, as an index into GROUNDS. */
  ground: Uint8Array
  /** Seat facing per tile index. */
  facing: Map<number, 1 | -1>
  portals: Map<number, Portal>
}

const grids = new Map<RoomId, Grid>()

/** The ground map of a room: painted zones, or the town's precomputed map. */
function groundOf(r: RoomDef): Uint8Array {
  const pre = (r as RoomDef & { groundMap?: Uint8Array }).groundMap
  if (pre) return pre
  const g = new Uint8Array(r.w * r.d).fill(GROUNDS.indexOf(r.ground))
  for (const [x, y, w, d, kind] of r.zones) {
    for (let i = Math.max(0, x); i < Math.min(r.w, x + w); i++) for (let j = Math.max(0, y); j < Math.min(r.d, y + d); j++) g[j * r.w + i] = GROUNDS.indexOf(kind)
  }
  return g
}

export function grid(id: RoomId): Grid {
  const hit = grids.get(id)
  if (hit) return hit
  const r = ROOMS[id]
  const ground = groundOf(r)
  const cells = new Uint8Array(r.w * r.d).fill(OPEN)
  for (let i = 0; i < cells.length; i++) if (WATER.has(GROUNDS[ground[i]!]!)) cells[i] = BLOCKED
  const facing = new Map<number, 1 | -1>()
  const portals = new Map<number, Portal>()
  for (const p of r.props) {
    const k = KINDS[p.kind]
    const { w, d } = footprint(p)
    if (!k.solid && !k.seat) continue
    for (let x = Math.floor(p.x); x < Math.ceil(p.x + w); x++) for (let y = Math.floor(p.y); y < Math.ceil(p.y + d); y++) {
      if (x < 0 || y < 0 || x >= r.w || y >= r.d) continue
      const i = y * r.w + x
      if (k.solid) cells[i] = BLOCKED
      else if (cells[i] !== BLOCKED) { cells[i] = SEAT; facing.set(i, p.face ?? 1) }
    }
  }
  for (const p of r.portals) { const i = p.y * r.w + p.x; cells[i] = OPEN; portals.set(i, p) }
  const g: Grid = { w: r.w, d: r.d, cells, ground, facing, portals }
  grids.set(id, g)
  return g
}

export function cellAt(g: Grid, x: number, y: number): number {
  return x < 0 || y < 0 || x >= g.w || y >= g.d ? BLOCKED : g.cells[y * g.w + x]!
}

export function groundAt(g: Grid, x: number, y: number): Ground {
  return x < 0 || y < 0 || x >= g.w || y >= g.d ? 'grass' : GROUNDS[g.ground[y * g.w + x]!]!
}

/** Where someone arriving in `room` from `from` stands. */
export function arrival(room: RoomId, from?: RoomId): [number, number] {
  const r = ROOMS[room]
  const p = from ? r.portals.find((q) => q.to === from) : undefined
  return p ? p.out : r.spawn
}
